import * as React from 'react';

import { cn } from '@/lib/utils';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  showTopBar?: boolean; // optional prop 추가
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, showTopBar = true, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-xl border bg-white text-card-foreground shadow',
        'bg-gradient-to-r from-slate-50 via-white to-blue-50/30 rounded-2xl',
        'border-l-4 border-l-blue-500 border border-slate-200/60',
        'shadow-lg shadow-slate-900/8 hover:shadow-xl hover:shadow-blue-900/15',
        'hover:border-blue-300/70',
        // 'hover:-translate-y-0.5 hover:scale-[1.01]',
        'transition-all duration-300 ease-out',
        'backdrop-blur-sm relative overflow-hidden',
        showTopBar &&
          'before:content-[""] before:absolute before:top-0 before:left-0 before:w-full before:h-1 before:bg-gradient-to-r before:from-blue-500 before:via-indigo-500 before:to-blue-600',
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-1.5 p-3 lg:p-6', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
  ),
);
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    // 위쪽 패딩은 CardHeader가 앞에 있을 때만 제거한다(중복 방지). CardHeader 없이
    // CardContent만 쓰면 첫 자식이 되어 상단 패딩이 유지되므로 그라디언트 바에 붙지 않는다.
    <div
      ref={ref}
      className={cn(
        'p-3 lg:p-6 [&:not(:first-child)]:pt-0 lg:[&:not(:first-child)]:pt-0',
        className,
      )}
      {...props}
    />
  ),
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center p-3 pt-0 lg:p-6 lg:pt-0', className)} {...props} />
  ),
);
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
